// src/components/TextFieldProgressBar.js
import React from "react";
import { Box, Typography, LinearProgress } from "@mui/material";

/**
 * TextFieldProgressBar
 *
 * Responsibility:
 *  - Show the moving guidance text above the bar
 *  - Show the black progress bar for 0 → hardLimit words
 *  - Show error text (only for "real" errors, not the hard-limit guidance)
 */
export default function TextFieldProgressBar({
  wordCount,
  minWords,
  maxWords,
  hardLimit,
  errorText,
  visualError,
}) {
  // 🧭 Guidance text state machine
  let guidanceLabel = "";
  let guidanceJustify = "flex-start";
  let guidanceFontSize = "0.75rem";
  let guidanceFontWeight = 400;

  if (wordCount < minWords) {
    guidanceLabel = `Write at least ${minWords} words…`;
    guidanceJustify = "flex-start";
    guidanceFontSize = "0.75rem";
    guidanceFontWeight = 400;
  } else if (wordCount < maxWords) {
    guidanceLabel = "Good, you can write more if needed.";
    guidanceJustify = "center";
    guidanceFontSize = "0.75rem";
    guidanceFontWeight = 400;
  } else if (wordCount < hardLimit) {
    guidanceLabel = "Almost at the limit.";
    guidanceJustify = "center";
    guidanceFontSize = "0.85rem"; // larger
    guidanceFontWeight = 400;     // not bold
  } else {
    guidanceLabel = "Too much — simplify.";
    guidanceJustify = "flex-end";
    guidanceFontSize = "0.9rem";  // larger
    guidanceFontWeight = 700;     // bold
  }
  // Progress value for the bar (0–100)
  const progressNormalized =
    hardLimit > 0 ? Math.min(wordCount / hardLimit, 1) : 0;

  return (
    <Box mt={1.5} mb={2.5}>
      {/* Guidance strip with flexible height so text can wrap safely */}
      <Box
        sx={{
          display: "flex",
          justifyContent: guidanceJustify,
          alignItems: "center",
          minHeight: 28, // enough for larger font + bold, expands if wrapped
          mb: 0.75,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontSize: guidanceFontSize,
            fontWeight: guidanceFontWeight,
            color: "#424242",
            textAlign: guidanceJustify === "flex-start" ? "left" : (guidanceJustify === "flex-end" ? "right" : "center"),
            lineHeight: 1.2,
          }}
        >
          {guidanceLabel}
        </Typography>
      </Box>

      {/* Progress bar */}
      <LinearProgress
        variant="determinate"
        value={progressNormalized * 100}
        sx={{
          height: 4,
          borderRadius: 999,
          backgroundColor: "#e0e0e0",
          "& .MuiLinearProgress-bar": {
            borderRadius: 999,
            backgroundColor: "#212121", // black bar
          },
        }}
      />

      {/* Error text only for non-"Too much" errors (e.g. long words) */}
      {visualError &&
        errorText &&
        !errorText.startsWith("Too much") && (
          <Typography
            variant="caption"
            color="error"
            sx={{ fontSize: "0.75rem", mt: 0.75 }}
          >
            {errorText}
          </Typography>
        )}
    </Box>
  );
}
